import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Package,
  Plus,
  Trash2,
  Lock,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import Header from "../components/Header";
import Navbar from "../components/Navbar";
import { fetchProducts, adminAddProduct, adminDeleteProduct } from "../api";
import { categories } from "../mock";

const emptyForm = {
  name: "",
  brand: "",
  price: "",
  mrp: "",
  off: 0,
  img: "",
  category: "medicines",
  section: "best",
  description: "",
  dosage: "",
};

const sections = [
  { value: "best", label: "Best Sellers" },
  { value: "new", label: "New Arrivals" },
  { value: "offers", label: "Top Offers" },
];

const Field = ({ label, ...props }) => (
  <div>
    <label className="block text-[11.5px] font-semibold text-gray-600 mb-1">{label}</label>
    <input
      {...props}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#12a150] focus:ring-2 focus:ring-[#12a150]/20"
    />
  </div>
);

const Admin = () => {
  const [key, setKey] = useState(() => sessionStorage.getItem("agm_admin_key") || "");
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("agm_admin_key"));
  const [keyInput, setKeyInput] = useState("");
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = () => fetchProducts({ limit: 200 }).then(setProducts).catch(() => {});

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  const unlock = () => {
    if (!keyInput.trim()) return;
    sessionStorage.setItem("agm_admin_key", keyInput.trim());
    setKey(keyInput.trim());
    setAuthed(true);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addProduct = async () => {
    if (!form.name.trim() || !form.price) {
      toast.error("Product name and price are required");
      return;
    }
    setBusy(true);
    try {
      await adminAddProduct(key, {
        ...form,
        price: Number(form.price),
        mrp: Number(form.mrp || form.price),
        off: Number(form.off || 0),
      });
      toast.success(`"${form.name}" added`);
      setForm(emptyForm);
      load();
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Invalid admin key");
        sessionStorage.removeItem("agm_admin_key");
        setAuthed(false);
      } else {
        toast.error("Failed to add product");
      }
    } finally {
      setBusy(false);
    }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await adminDeleteProduct(key, p.id);
      toast.success("Product deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#f6faf7]">
      <Header variant="home" />
      <Navbar />

      <div className="max-w-[1000px] mx-auto px-3 lg:px-4 py-6">
        <div className="flex items-center gap-1.5 text-[11.5px] text-gray-500 mb-3">
          <Link to="/" className="hover:text-[#0e7a3d]">Home</Link>
          <ChevronRight size={12} />
          <span className="font-semibold text-gray-700">Admin · Manage Products</span>
        </div>

        {!authed ? (
          <div className="max-w-[380px] mx-auto mt-16 bg-white border border-gray-100 rounded-2xl shadow-sm p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <Lock size={24} className="text-[#12a150]" />
            </div>
            <h1 className="text-[18px] font-bold text-gray-800">Admin Access</h1>
            <p className="text-[12.5px] text-gray-400 mt-1 mb-4">Enter the admin key to manage products</p>
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && unlock()}
              placeholder="Admin key"
              className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13.5px] outline-none focus:border-[#12a150] focus:ring-2 focus:ring-[#12a150]/20 mb-3"
            />
            <button
              onClick={unlock}
              className="w-full bg-[#12a150] hover:bg-[#0d8a40] text-white text-[14px] font-bold rounded-full py-2.5 transition-colors"
            >
              Unlock
            </button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[380px_1fr] gap-5">
            {/* Add product form */}
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 h-fit">
              <h2 className="flex items-center gap-2 text-[16px] font-bold text-gray-800 mb-4">
                <Plus size={18} className="text-[#12a150]" /> Add New Product
              </h2>
              <div className="space-y-3">
                <Field label="Product name *" value={form.name} onChange={set("name")} placeholder="e.g. Paracetamol 500mg" />
                <Field label="Brand" value={form.brand} onChange={set("brand")} placeholder="e.g. Cipla" />
                <div className="grid grid-cols-3 gap-2.5">
                  <Field label="Price (₹) *" type="number" value={form.price} onChange={set("price")} placeholder="25" />
                  <Field label="MRP (₹)" type="number" value={form.mrp} onChange={set("mrp")} placeholder="28" />
                  <Field label="% Off" type="number" value={form.off} onChange={set("off")} placeholder="10" />
                </div>
                <Field label="Image URL" value={form.img} onChange={set("img")} placeholder="https://..." />
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="block text-[11.5px] font-semibold text-gray-600 mb-1">Category</label>
                    <select
                      value={form.category}
                      onChange={set("category")}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#12a150] bg-white"
                    >
                      {categories.map((c) => (
                        <option key={c.slug} value={c.slug}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11.5px] font-semibold text-gray-600 mb-1">Section</label>
                    <select
                      value={form.section}
                      onChange={set("section")}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#12a150] bg-white"
                    >
                      {sections.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[11.5px] font-semibold text-gray-600 mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={set("description")}
                    rows={2}
                    placeholder="Short product description"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#12a150] focus:ring-2 focus:ring-[#12a150]/20"
                  />
                </div>
                <Field label="Dosage / Usage" value={form.dosage} onChange={set("dosage")} placeholder="e.g. 1 tablet twice daily" />
                <button
                  onClick={addProduct}
                  disabled={busy}
                  className="w-full bg-[#12a150] hover:bg-[#0d8a40] disabled:opacity-60 text-white text-[14px] font-bold rounded-full py-2.5 transition-colors"
                >
                  {busy ? "Adding..." : "Add Product"}
                </button>
              </div>
            </div>

            {/* Product list */}
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="flex items-center gap-2 text-[16px] font-bold text-gray-800">
                  <Package size={18} className="text-[#12a150]" /> All Products ({products.length})
                </h2>
                <button
                  onClick={load}
                  className="flex items-center gap-1 text-[12px] font-semibold text-[#0e7a3d] hover:underline"
                >
                  <RefreshCw size={13} /> Refresh
                </button>
              </div>
              <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
                {products.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 border border-gray-100 rounded-xl p-2.5 hover:bg-gray-50">
                    <img src={p.img} alt="" className="w-11 h-11 rounded-lg object-cover bg-gray-100" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-gray-800 truncate">{p.name}</div>
                      <div className="text-[11px] text-gray-400">
                        #{p.id} · {p.brand} · ₹{p.price} · {p.category}
                      </div>
                    </div>
                    <button
                      onClick={() => removeProduct(p)}
                      className="text-gray-300 hover:text-red-500 transition-colors p-1"
                      aria-label="Delete product"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                {products.length === 0 && (
                  <div className="text-center text-gray-400 text-[13px] py-10">No products yet</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
